import DOMPurify from "dompurify";
import styles from "./UserProfile.module.css";
import {
  Flex,
  Input,
  Form,
  ConfigProvider,
  Button,
  Layout,
  Row,
  Col,
  Modal,
  Card,
} from "antd";
import Collapsible from "../Collapsible/Collapsible";
import DefaultCompanyLogo from "../../../assets/img/Logo-Black-100px.png";
import { getCookie } from "../../../utils/cookies";
import { showToast } from "../../Toaster/Toaster";
import {
  getAuth,
  onAuthStateChanged,
  OAuthProvider,
  linkWithPopup,
  unlink,
} from "firebase/auth";
import { useEffect, useState } from "react";
import {
  getUserCompaniesByUID,
  updateUserSSOStatus,
} from "../../../services/auth.service";
import { getFormattedDate } from "../../../utils/time";
import { getUserImages } from "../../../services/common.service";
import GreenTick from "../../../assets/img/green-tick.png";

const UserProfile = () => {
  const accessToken = getCookie("firebase_token") as string;
  const chUserDataCookie = localStorage.getItem("ch_user_data");
  const userData = JSON.parse(chUserDataCookie as string);
  const [userCompanies, setUserCompanies] = useState<any[]>([]);
  const [isAccessLevelModalOpen, setIsAccessLevelModalOpen] = useState(false);
  const [userAccessLevels, setUserAccessLevels] = useState<any[]>([]);
  const [userApps, setUserApps] = useState<any[]>([]);
  const [isAppsModalOpen, setIsAppsModalOpen] = useState(false);
  const [isSitesModalOpen, setIsSitesModalOpen] = useState(false);
  const [userSites, setUserSites] = useState<any[]>([]);
  const [isAgencyLocationModalOpen, setIsAgencyLocationModalOpen] =
    useState(false);
  const [userAgencyLocations, setUserAgencyLocations] = useState<any[]>([]);
  const [parentCompanyLogo, setParentCompanyLogo] =
    useState<any>(DefaultCompanyLogo);
  const [companyLogo, setCompanyLogo] = useState<any>(DefaultCompanyLogo);
  const auth = getAuth();

  const connectMicrosoft = async () => {
    const provider = new OAuthProvider("microsoft.com");
    const user: any = auth.currentUser;

    linkWithPopup(user, provider)
      .then(async (newUser: any) => {
        if (
          newUser._tokenResponse.hasOwnProperty("email") &&
          newUser._tokenResponse.email?.toLowerCase() !==
            user.email.toLowerCase()
        ) {
          unlink(user, "microsoft.com").then(() => {});
          showToast(
            "Email linked with your account and your Microsoft account is different. Please use the same email account to connect.",
            { type: "error" }
          );
        } else {
          showToast(
            "Connected your microsoft account to your existing user account.",
            { type: "success" }
          );
          const response = await updateUserSSOStatus(
            accessToken,
            userData._id,
            true
          );
          if (response.data.success && response.data.data) {
            showToast("Successfully connected microsoft account.", {
              type: "success",
            });
            window.location.reload();
          }
        }
      })
      .catch(() => {
        showToast("Failed to connect account.", { type: "error" });
      });
  };

  const unlinkMicrosoft = async () => {
    try {
      const user: any = auth.currentUser;
      await unlink(user, "microsoft.com");

      const response = await updateUserSSOStatus(
        accessToken,
        userData._id,
        false
      );
      if (response.data.success && response.data.data) {
        showToast("Successfully disconnected microsoft account.", {
          type: "success",
        });
        window.location.reload();
      }
    } catch (err) {
      showToast("Failed to disconnect microsoft account.", { type: "error" });
    }
  };
  const getImage = async (type: string, id: string) => {
    const response = await getUserImages(type, [id], accessToken);
    setParentCompanyLogo(
      response.data[0]?.image?.imageSmall || DefaultCompanyLogo
    );
  };

  const getUserImage = async (type: string, id: string) => {
    const response = await getUserImages(type, [id], accessToken);
    setCompanyLogo(response?.data[0]?.image?.imageSmall || DefaultCompanyLogo);
  };

  useEffect(() => {
    getUserImage(
      userData?.provider?._id ? "provider" : "agency",
      userData?.provider?._id || userData?.agency?._id
    );
    if (userData.provider || userData.agency) {
      const orgType = userData.provider ? "provider" : "agency";
      const orgId = userData.provider?._id || userData.agency?._id;

      getUserCompaniesByUID(userData._id, orgType, orgId, accessToken).then(
        (response) => {
          setUserCompanies(response.data);
        }
      );

      getImage(
        "parent-company",
        userData?.provider?.parentCompany?._id ||
          userData?.agency?.parentCompany?._id
      );
    }
  }, []);

  const openUserAccessLevelsModal = (groups: any[]) => {
    setUserAccessLevels(groups);
    setIsAccessLevelModalOpen(true);
  };
  const openAppsModal = (apps: any[]) => {
    setUserApps(apps);
    setIsAppsModalOpen(true);
  }
  const openSitesModal = (sites: any[]) => {
    setUserSites(sites);
    setIsSitesModalOpen(true);
  };

  const openAgencyLocationModal = (locations: any[]) => {
    setUserAgencyLocations(locations);
    setIsAgencyLocationModalOpen(true);
  };

  return (
    <div style={{ backgroundColor: "white" }} className={styles["profile-container"]}>
      <div className={styles["scrollable-container"]}>
        <Row
          style={{
            marginBottom: 20,
            display: "flex",
            justifyContent: "space-between",
            padding: "0 20px",
          }}
        >
          <Col span={5}>
            <div className={styles["profile-card"]}>
              <div className={styles["logo-container"]}>
                <img className={styles["company-logo"]} src={companyLogo} />
              </div>
              <p className={styles["profile-name-text"]}>
                {userData?.fullName}
              </p>
              <p className={styles["profile-email-text"]}>{userData?.email}</p>
              {!(userData?.provider?.enforceSSO === true) && userData.sso && (
                <p
                  onClick={unlinkMicrosoft}
                  className={styles["microsoft-btn"]}
                >
                  Disconnect Microsoft Account
                </p>
              )}
              {!userData.sso && (
                <p
                  onClick={connectMicrosoft}
                  className={styles["microsoft-btn"]}
                >
                  Connect Microsoft Account
                </p>
              )}
            </div>
          </Col>
          <Col span={18}>
            <Collapsible title="Preferences" expanded={true}>
              <ConfigProvider
                theme={{
                  components: {
                    Form: {
                      labelColor: "#536590",
                      labelFontSize: 16,
                    },
                  },
                }}
              >
                <Form layout="vertical">
                  <Flex
                    gap="middle"
                    className={styles["input-lable"]}
                    align="start"
                  >
                    <Form.Item style={{ width: "50%" }} label="Language">
                      <Input value="English" disabled={true} />
                    </Form.Item>
                    <Form.Item style={{ width: "50%" }} label="Time zone">
                      <Input value="Europe/London" disabled={true} />
                    </Form.Item>
                  </Flex>
                </Form>
              </ConfigProvider>
              <ConfigProvider
                theme={{
                  components: {
                    Form: {
                      labelColor: "#536590",
                      labelFontSize: 16,
                    },
                  },
                }}
              >
                <Form layout="vertical">
                  <Flex
                    gap="middle"
                    className={styles["input-lable"]}
                    align="start"
                  >
                    <Form.Item style={{ width: "50%" }} label="Currency">
                      <Input value="GBP" disabled={true} />
                    </Form.Item>
                    <Form.Item style={{ width: "50%" }} label="Auto Logout">
                      <Input value="1 hour" disabled={true} />
                    </Form.Item>
                  </Flex>
                </Form>
              </ConfigProvider>
              <ConfigProvider
                theme={{
                  components: {
                    Form: {
                      labelColor: "#536590",
                      labelFontSize: 16,
                    },
                  },
                }}
              >
                <Form layout="vertical">
                  <Flex
                    gap="middle"
                    className={styles["input-lable"]}
                    align="start"
                  >
                    <Form.Item
                      style={{ width: "50%" }}
                      label="Default Platform"
                    >
                      <Input value="SCM" disabled={true} />
                    </Form.Item>
                  </Flex>
                </Form>
              </ConfigProvider>
              <p className={styles["preferences-text"]}>
                If you need to make any changes to the above configuration,
                please contact techsupport@carehires.com
              </p>
            </Collapsible>
          </Col>
        </Row>
        {(userData?.agency || userData?.provider) && (
          <div style={{ padding: "0 20px" }}>
            <Collapsible title="Connected Organisations" expanded={true}>
              <Row className={styles["table-header"]}>
                <Col span={6} className={styles["table-header-item"]}>
                  Organisation Name
                </Col>
                <Col span={6} className={styles["table-header-item"]}>
                  {userData?.agency ? "Location/City" : "Sites"}
                </Col>
                <Col span={6} className={styles["table-header-item"]}>
                  Joined Date
                </Col>
                <Col span={3} className={styles["table-header-item"]}>
                  Authenticated Apps
                </Col>
              </Row>
              {userCompanies.map((company) => (
                <Row key={company.id} className={styles["table-body"]}>
                  <Col span={6} className={styles["table-body-item"]}>
                    {company?.provider?.companyName ||
                      company?.agency?.businessName}
                  </Col>
                  {userData?.agency && (
                    <Col span={6} className={styles["table-body-item"]}>
                      <div
                        className={styles["text-btn"]}
                        onClick={() =>
                          openAgencyLocationModal(company.locations)
                        }
                      >
                        {company.locations.length} locations
                      </div>
                    </Col>
                  )}
                  {userData?.provider && (
                    <Col span={6} className={styles["table-body-item"]}>
                      <div
                        className={styles["text-btn"]}
                        onClick={() => openSitesModal(company.careHomes)}
                      >
                        {company.careHomes.length} sites
                      </div>
                    </Col>
                  )}
                  <Col span={6} className={styles["table-body-item"]}>
                    {getFormattedDate(company.createdAt, "DD/MM/YYYY")}
                  </Col>
                  <Col span={3} className={styles["table-body-item"]}>
                  <div
                      className={styles["text-btn"]}
                      onClick={() =>
                        openAppsModal(
                          company.apps
                        )
                      }
                    >
                      {company.apps.length} apps
                    </div>
                  </Col>
                </Row>
              ))}
            </Collapsible>
          </div>
        )}
      </div>
      <Modal
        centered
        title={
          <div className={styles["modal-title"]}>
            Assigned User Access Levels
          </div>
        }
        open={isAccessLevelModalOpen}
        onCancel={() => setIsAccessLevelModalOpen(false)}
        footer={null}
      >
        <Row className={styles["table-header"]}>
          <Col span={12} className={styles["table-header-item"]}>
            Access Level
          </Col>
        </Row>
        <div
          className={styles["scrollable-container"]}
          style={{ height: "25vh" }}
        >
          {userAccessLevels.map((accessLevel) => (
            <Row key={accessLevel} className={styles["table-body"]}>
              <Col span={12} className={styles["table-body-item"]}>
                {accessLevel}
              </Col>
            </Row>
          ))}
        </div>
      </Modal>
      <Modal
        centered
        title={
          <div className={styles["modal-title"]}>
            Authenticated Applications
          </div>
        }
        open={isAppsModalOpen}
        onCancel={() => setIsAppsModalOpen(false)}
        footer={null}
        width="fit-content"
      >
        <div
          className={styles["scrollable-container"]}
          style={{ height: "fit-content" }}
        >
          {userApps.map((app) => (
            <div className="mb-2" key={app?.appId?._id} style={{ padding: '10px', backgroundColor: '#EFF4FE' }}>
              <div className="d-flex align-items-center">
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(app?.appId?.nameHtml ?? "") }}></div>
                <span className="ms-2" style={{display: 'inline-block', width: '4px', height: '4px', backgroundColor: '#78777E', borderRadius: '50%', marginBottom: '0.5rem'}}></span>
                <p className="app-name ms-2" style={{marginBottom: '0.5rem'}}>{app?.appId?.subName}</p>
              </div>
              <p className="mt-1 app-description">{app?.appId?.description}</p>
              <div className="row mt-2">
                <div className="col-md-8">
                  {app?.groups?.length > 0 ? (
                    <div
                      className={styles["text-btn"]}
                      onClick={() =>
                        openUserAccessLevelsModal(
                          app.groups.map((group: any) => group.name)
                        )
                      }
                    >
                      {app?.groups?.length} Group{app?.groups?.length > 1 ? 's' : ''}
                    </div>
                  ) : (
                    <p style={{fontWeight: 'bold'}}>No Groups</p>
                  )}
                </div>
                <div className="col-md-4 d-flex justify-content-end">
                  {app.isAuthorizer && (
                    <img
                      src={GreenTick}
                      style={{ width: "22px", height: "22px" }}
                      className="me-2"
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Modal>
      <Modal
        centered
        title={
          <div className={styles["modal-title"]}>Assigned Site Location(s)</div>
        }
        open={isSitesModalOpen}
        onCancel={() => setIsSitesModalOpen(false)}
        footer={null}
        width={"40%"}
      >
        <Row className={styles["table-header"]}>
          <Col span={8} className={styles["table-header-item"]}>
            Site name
          </Col>
          <Col span={8} className={styles["table-header-item"]}>
            Site code
          </Col>
          <Col span={8} className={styles["table-header-item"]}>
            Site Status
          </Col>
        </Row>
        <div
          className={styles["scrollable-container"]}
          style={{ height: "25vh" }}
        >
          {userSites.map((site) => (
            <Row key={site.id} className={styles["table-body"]}>
              <Col span={8} className={styles["table-body-item"]}>
                {site.careHomeName}
              </Col>
              <Col span={8} className={styles["table-body-item"]}>
                {site.careHomeCode}
              </Col>
              <Col span={8} className={styles["table-body-item"]}>
                {site?.status == "active" ? (<span style={{color: '#1D4ED8', fontWeight: 600}}>Active</span>) : (<span style={{color: 'red', fontWeight: 600}}>Inactive</span>)}
              </Col>
            </Row>
          ))}
        </div>
      </Modal>
      <Modal
        centered
        title={
          <div className={styles["modal-title"]}>Assigned Site Location(s)</div>
        }
        open={isAgencyLocationModalOpen}
        onCancel={() => setIsAgencyLocationModalOpen(false)}
        footer={null}
      >
        <Row className={styles["table-header"]}>
          <Col span={12} className={styles["table-header-item"]}>
            Location
          </Col>
          <Col span={12} className={styles["table-header-item"]}>
            City
          </Col>
        </Row>
        <div
          className={styles["scrollable-container"]}
          style={{ height: "25vh" }}
        >
          {userAgencyLocations.map((location) => (
            <Row key={location._id} className={styles["table-body"]}>
              <Col span={12} className={styles["table-body-item"]}>
                {location.name}
              </Col>
              <Col span={12} className={styles["table-body-item"]}>
                {location.city}
              </Col>
            </Row>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default UserProfile;
